import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageLoader } from '@customerio/pluma-components/react';
import { SafeNavigate } from '@/services/router/safe-navigate';
import {
  fetchCurrentAccountUncached,
  seedCurrentAccountCache,
  type AccountResponse,
} from '@/services/auth/account-api';
import { getEnvironment } from '@/services/db/environment/queries';
// Source module import (not the barrel) to match this page's pre-auth import graph.
import { getApiClientErrorStatus } from '@/services/api/errors';
// oxlint-disable-next-line local-rules/enforce-feature-boundaries -- the barrel would pull the copilot runtime into the signup chunk
import { selectPostSignupDestination } from '@/features/onboarding/post-signup-destination';
import { trackSignupCompleted } from './utils/analytics';
import type { SignupCompletion } from './types';

const SIGNUP_POLLING_DELAY_MS = 1000;

/**
 * Readiness itself is retried without limit — provisioning time is the
 * server's, not ours to cap. A failing request is a different thing: past this
 * many consecutive ones, the app the redirect leads to is broken too, and its
 * own bootstrap reports that far better than a loader that never resolves.
 */
export const MAX_TRANSPORT_FAILURES = 3;

/** The account exists but its workspace isn't usable yet — retry, don't give up. */
class AccountNotReadyError extends Error {}

/**
 * The new user's default workspace: the earliest-created environment the user
 * can access. Right after account creation the payload can arrive without one
 * — that's the "not ready yet" signal, not an error state.
 */
function resolveDefaultEnvironmentId(response: AccountResponse): string | null {
  const accessibleIds = new Set(response.user?.accessible_environment_ids ?? []);
  const candidates = (response.environments ?? []).filter(
    (environment) => accessibleIds.has(environment.id) && environment.created_at != null,
  );
  candidates.sort((a, b) => a.created_at - b.created_at);
  return candidates[0]?.id ?? null;
}

async function checkAccountReady(signal: AbortSignal): Promise<AccountResponse> {
  const response = await fetchCurrentAccountUncached(signal);
  const environmentId = resolveDefaultEnvironmentId(response);
  if (environmentId == null) {
    throw new AccountNotReadyError('new account has no accessible environment yet');
  }
  try {
    await getEnvironment(Number(environmentId), signal);
  } catch (error) {
    // The account payload can list the workspace before it is queryable — a 404
    // here is the same "not ready yet" signal as a missing id, not a failure.
    if (getApiClientErrorStatus(error) === 404) {
      throw new AccountNotReadyError('new workspace is not queryable yet');
    }
    throw error;
  }
  return response;
}

type PostRegistrationWaitProps = {
  completion: SignupCompletion;
};

/**
 * Post-registration hold: right after the account is created, its environment
 * tables may not exist yet, so entering the app immediately can land on a
 * half-provisioned dashboard. Poll until both the account payload carries an
 * accessible environment and that environment loads, fire `Signup: Completed`,
 * then redirect into the app.
 *
 * On readiness the good payload is seeded into the cross-app account cache —
 * polling must bypass that cache (a not-yet-ready first response winning its
 * `cacheOnlyOnce` slot would misroute the bootstrap), but the bootstrap and
 * the embedded apps should reuse the ready one.
 */
export function PostRegistrationWait({ completion }: PostRegistrationWaitProps) {
  // `failureCount` counts every failure, readiness ones included, so the
  // transport budget is tracked on its own.
  const transportFailuresRef = useRef(0);

  // Kept rather than reduced to a boolean: the landing destination reads it.
  const { data: readyResponse, isError } = useQuery({
    queryKey: ['signup', 'post-registration-readiness'],
    queryFn: async ({ signal }) => {
      const response = await checkAccountReady(signal);
      seedCurrentAccountCache(response);
      return response;
    },
    // Unmount aborts the in-flight attempt via the query signal.
    retry: (_failureCount, error) => {
      if (error instanceof AccountNotReadyError) {
        // The request itself succeeded, so the failure budget starts over.
        transportFailuresRef.current = 0;
        return true;
      }
      transportFailuresRef.current += 1;
      return transportFailuresRef.current <= MAX_TRANSPORT_FAILURES;
    },
    retryDelay: SIGNUP_POLLING_DELAY_MS,
    staleTime: 0,
    gcTime: 0,
  });

  const isReady = readyResponse != null;

  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (!isReady || hasTrackedRef.current) {
      return;
    }
    hasTrackedRef.current = true;
    trackSignupCompleted(completion);
  }, [isReady, completion]);

  // A poll that gave up redirects without the event: the account was created,
  // but readiness was never observed, and Ember only fired on a good poll.
  if (!isReady && !isError) {
    return <PageLoader label="Loading…" />;
  }

  // The give-up path has no payload to choose from.
  const destination = readyResponse?.account == null ? '/' : selectPostSignupDestination(readyResponse.account);
  return <SafeNavigate to={destination} options={{ shouldReplaceHistoryEntry: true }} />;
}
